import * as L from 'leaflet'

import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'

import { PolylineMeasureExtend } from '@map-sdk/sdk/plugins/PolylineMeasureExtend'
import { CanvasShipService } from '@map-sdk/sdk/CanvasShipService'
import { canvasShip } from '@map-sdk/sdk/CanvasShipSJService'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { areaViewSymbol } from '@map-sdk/sdk/plugins/AreaView/AreaViewSymbol'
import attributionElane from '@map-sdk/sdk/plugins/AttributionElane'
import { latlngGraticuleServer } from '@map-sdk/sdk/plugins/LatLngGraticuleServer'
import ScaleElane from '@map-sdk/sdk/plugins/ScaleElane'
import { WeatherServiceImpl } from '@map-sdk/sdk/plugins/weather/WeatherServiceImpl'
import { NavigationServiceImpl } from '@map-sdk/sdk/plugins/NavigationServiceImpl'
import { PortCanvas } from '@map-sdk/sdk/plugins/Port/PortCanvas'
import { TrackServiceImpl } from '@map-sdk/sdk/plugins/Trackplayer/TrackServiceImpl'
import { mousePosition } from '@map-sdk/sdk/plugins/utils/mousePositong'
import { TrackForecast } from '@map-sdk/sdk/plugins/Trackplayer/TrackForecast'
import { NavigationalWarningService } from '@map-sdk/sdk/plugins/NavigationalWarning/NavigationalWarningService'
import {
  type TrackDrawServiceImpl,
  trackDrawServiceImpl,
} from '@map-sdk/sdk/plugins/TrackDraw/TrackDrawServiceImpl'
import { DrawUtil } from '@map-sdk/sdk/plugins/Draw/DrawUtils'
import { OverLayers } from '@map-sdk/sdk/plugins/Draw/OverLayers'
import {
  type AisManageOptions,
  AisManager,
} from '@map-sdk/sdk/plugins/Ais/AisManager'
import { terminatorSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/TerminatorSymbol'
import type { CanvasShipServiceOptions } from '@map-sdk/sdk/CanvasShipService'
import type {
  LatLngGraticuleServer,
  OptionsLatLngGraticule,
} from '@map-sdk/sdk/plugins/LatLngGraticuleServer'
import type {
  AreaViewSymbol,
  AreaViewSymbolOptions,
} from '@map-sdk/sdk/plugins/AreaView/AreaViewSymbol'

type TileSource = 'sea' | 'google' | 'satellite'
type PartialTileSource = {
  [K in TileSource]?: TileUrlRecord
}

type TileUrlRecord = Record<
  'url' | 'errorTileUrl',
  string | Record<number, { url: string }>
>

/**
 * 地图接口参数
 */
export interface ShipxyAPIOptions extends L.MapOptions {
  attribution?: { isShow: boolean; emptyString: string }
  mapTypes?: string[]
  ak?: string
  defaultMapType?: string
  gratings?: { isShow: boolean; maxZoom: number; type: string }
  centerPoint?: [number, number]
  measureCtrl?: { isShow: boolean; position: string }
  mousePostionCtrl?: { isShow: boolean; position: string }
  zoomControlElane?: { isShow: boolean; position: string }
  zoomviewControl?: { isShow: boolean; position: string }
  basemapsControl?: { isShow: boolean; position: string }
  mapReadyCallBack?: (map: L.Map) => void
  scaleCtrl?: { isShow: boolean; position: string }
  addTileLayer?: L.TileLayer[]
  cjhdTileLayer?: { isShow: boolean }
  miniMapControl?: { isShow: boolean; options?: any }
  tileLayer?: L.TileLayerOptions & PartialTileSource
}
interface MyMapInterface {
  getMapType: () => string
}
export type MyMapType = MyMap
export class MyMap extends L.Map implements MyMapInterface {
  [x: string]: any
  public static service = []
  public _options?: ShipxyAPIOptions
  public _hoverShipId: any
  public offsetC = true
  public _selShipId!: Record<string, any>
  public _size?: L.Point
  public _unselectedShipListener?: (params?: any) => void
  public mapType?: string
  public shipxy_tooltip?: L.Tooltip
  public shipxy_tooltip_timer?: ReturnType<typeof setTimeout>
  weatherService!: WeatherServiceImpl
  shipsService!: CanvasShipService
  latlngGraticuleServer!: LatLngGraticuleServer
  areaViewSymbol!: AreaViewSymbol
  navigationService!: NavigationServiceImpl
  portCanvasService!: PortCanvas
  trackService!: TrackServiceImpl
  trackForecast!: TrackForecast
  public static readonly defaultOptions = {
    // 公司版权信息( 支持html )，默认Elane Inc.
    attribution: {
      isShow: true,
      emptyString: '&copy;2024 <a class="">LandSea</a><a></a>',
    },
    attributionControl: false,
    // 显示地图类型，'MT_SEA'，MT_GOOGLE,MT_SATELLITE,
    mapTypes: ['MT_SEA', 'MT_GOOGLE', 'MT_SATELLITE'],
    // 授权码
    ak: '',
    // 默认展示地图类型
    defaultMapType: 'MT_GOOGLE',
    // 光栅图,是否显示，最大显示级别,默认:false
    gratings: {
      isShow: false,
      maxZoom: 9,
      type: 'RASTER_WORLD',
    },
    // 初始中心点坐标
    centerPoint: [32.1, 122.11],

    // 初始缩放级别
    zoom: 4,
    // 最小缩放级别
    minZoom: 2,
    // 最大缩放级别
    maxZoom: 18,
    // 测量控件的显示隐藏
    measureCtrl: {
      isShow: true,
      position: 'topleft',
    },

    // 鼠标移动悬浮经纬度控件
    mousePostionCtrl: {
      isShow: true,
      position: 'bottomright',
    },
    // 缩放控件的显示隐藏
    zoomControlElane: {
      isShow: true,
      position: 'topright',
    },
    // 缩放级别显示控件
    zoomviewControl: {
      isShow: true,
      position: 'topleft',
    },
    // 地图切换控件的位置
    basemapsControl: {
      isShow: true,
      position: 'topright',
    },

    // 比例尺，控件
    scaleCtrl: {
      isShow: true,
      position: 'bottomleft',
    },
    // 自定义图源
    addTileLayer: [],
    // 是否显示长江航道，默认：false,
    cjhdTileLayer: {
      isShow: false,
    },
    // 鹰眼控件
    miniMapControl: {
      isShow: false,
      options: {},
    },
    // 默认图源设置（url，errorTileUrl），默认图源类型有：sea、google、satellite
    tileLayer: null,
    zoomControl: false,
    worldCopyJump: true,
    preferCanvas: false,
    wheelPxPerZoomLevel: 800,
    zoomAnimation: false,
    markerZoomAnimation: false,
    zoomSnap: 1,
    zoomDelta: 1,
    fadeAnimation: true,
    wheelDebounceTime: 40,
  }
  constructor(id: string | HTMLElement, options?: ShipxyAPIOptions) {
    super(id, options)
    this._options = options
  }
  getMapType() {
    const id = this.getContainer().id
    let type = CommUtils.getCookie(`_sdk_maptype_${id}`)
    if (CommUtils.isEmpty(type)) {
      type = this._options?.defaultMapType
      CommUtils.setCookie(`_sdk_maptype_${id}`, type)
    }
    return type
  }
}

/**
 * 公共接口
 */
export class ShipxyAPI {
  public static Map = MyMap
  public static Control = {}

  constructor() {
    // 假设这里进行了ShipxyAPI.Map相关的初始化工作
  }

  static version = `${ShipxyOptions.versionType}-3.5.202308251430`
  static GeoJSON = L.GeoJSON
  static geoJSON = L.geoJSON
  static geoJson = L.geoJson
  static Layer = L.Layer
  static LayerGroup = L.LayerGroup
  static layerGroup = L.layerGroup
  static FeatureGroup = L.FeatureGroup
  static featureGroup = L.featureGroup
  static LatLng = L.LatLng
  static latLng = L.latLng
  static Marker = L.Marker
  static marker = L.marker
  static Circle = L.Circle
  static circle = L.circle
  static circleMarker = L.circleMarker
  static Polyline = L.Polyline
  static polyline = L.polyline
  static Polygon = L.Polygon
  static polygon = L.polygon
  static Rectangle = L.Rectangle
  static rectangle = L.rectangle
  static DivIcon = L.DivIcon
  static divIcon = L.divIcon
  static extend = L.extend
  static DomUtil = L.DomUtil
  static Util = L.Util
  static Browser = L.Browser
  static console = () => {
    const consoleListFunction = {} as {
      log: (...params: any) => object
      debug: (...params: any) => object
      info: (...params: any) => object
      warn: (...params: any) => object
      error: (...params: any) => object
      assert: (...params: any) => object
      dir: (...params: any) => object
      dirxml: (...params: any) => object
      group: (...params: any) => object
      groupEnd: (...params: any) => object
      time: (...params: any) => object
      timeEnd: (...params: any) => object
      count: (...params: any) => object
      trace: (...params: any) => object
      profile: (...params: any) => object
      profileEnd: (...params: any) => object
      memory: (...params: any) => object
    }
    const consoleList = [
      'log',
      'debug',
      'info',
      'warn',
      'error',
      'assert',
      'dir',
      'dirxml',
      'group',
      'groupEnd',
      'time',
      'timeEnd',
      'count',
      'trace',
      'profile',
      'profileEnd',
      'memory',
    ]
    for (const element of consoleList) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      ;(consoleListFunction as any)[element] = function () {}
    }
    if (ShipxyOptions.log && window.console) {
      for (let i = 0; i < ShipxyOptions.log.length; i++) {
        const log = ShipxyOptions.log[i]
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        log && log.length > 0 && ((consoleListFunction as any)[log] = () => {}) //window.console[log]
      }
    }
    return consoleListFunction
  }

  static _onAddController(map: any, _this: any): void {
    const setOrder = (order: number | undefined): void => {
      if (order !== undefined && _this._container) {
        _this._container.style.order = order.toString()
      }
    }

    if ('_zoomInButton' in _this) {
      if (map.options.zoomControlElane && map.options.zoomControlElane.order) {
        setOrder(map.options.zoomControlElane.order)
      }
    } else if (_this._container.classList.contains('leaflet-control-minimap')) {
      if (map.options.miniMapControl && map.options.miniMapControl.order) {
        setOrder(map.options.miniMapControl.order)
      }
    } else if (_this.options && _this.options.order) {
      setOrder(_this.options.order)
    }
  }

  static addDefaultHooks() {
    ShipxyAPI.Map.include({
      mapType: '',
      _mapLayers: {},
      getMapType() {
        const id = this.getContainer().id
        let type = CommUtils.getCookie(`_sdk_maptype_${id}`)
        if (CommUtils.isEmpty(type)) {
          type = this.options.defaultMapType
          CommUtils.setCookie(`_sdk_maptype_${id}`, type)
        }
        return type
      },
    })

    ShipxyAPI.Map.mergeOptions(MyMap.defaultOptions)

    /**
     * 初始化地图控件
     */
    ShipxyAPI.Map.addInitHook(function () {
      ShipxyOptions.providerTiles.RASTER_WORLD =
        ShipxyOptions.providerTiles.RASTER_WORLD.replace('{k}', this.options.ak)
      ShipxyOptions.providerTiles.RASTER_WORLD_GOOGLE =
        ShipxyOptions.providerTiles.RASTER_WORLD_GOOGLE.replace(
          '{k}',
          this.options.ak
        )

      this.mapType = this.getMapType()

      this.setView(
        this.options.centerPoint || [32.1, 122.11],
        this.options.zoom || 4
      )
      this.setMaxBounds(L.latLngBounds([-85, -3600], [85, 3600]))
      this.setMinZoom(this.options.minZoom || 2)
      this.setMaxZoom(this.options.maxZoom || 18)
      if (this.options.attribution.isShow) {
        this.attributionControl && this.removeControl(this.attributionControl)

        this.attributionControl = attributionElane({
          emptyString: this.options.attribution.emptyString,
        }).addTo(this)
      }
      if (this.options.scaleCtrl.isShow) {
        this.scaleElaneControl && this.removeControl(this.scaleElaneControl)
        this.scaleElaneControl = new ScaleElane({
          nautic: true,
          imperial: false,
          metric: false,
        }).addTo(this)
        ShipxyAPI._onAddController(this, this.scaleElaneControl)
      }
      if (this.options.mousePostionCtrl.isShow) {
        this.positionControl && this.removeControl(this.positionControl)
        this.positionControl = mousePosition({
          position: 'bottomleft',
          separator: ' <br/>',
          emptyString: `${CanvasShipUtils.formatDegreesToDMS(
            this.options.centerPoint[0],
            true
          )}<br/>${CanvasShipUtils.formatDegreesToDMS(
            this.options.centerPoint[1],
            false
          )}`,
          lngFormatter: (value: number): string =>
            CanvasShipUtils.formatDegreesToDMS(value, true),

          latFormatter: (value: number): string =>
            CanvasShipUtils.formatDegreesToDMS(value, false),
        })
        this.addControl(this.positionControl)
        ShipxyAPI._onAddController(this, this.positionControl)
      }
      if (this.options.zoomControlElane.isShow) {
        const position = this.options.zoomControlElane.position
          ? this.options.zoomControlElane.position
          : 'topright'
        this.zoomControl && this.removeControl(this.zoomControl)
        this.zoomControl = L.control
          .zoom({
            position,
            zoomInTitle: '放大',
            zoomOutTitle: '缩小',
          })
          .addTo(this)
        ShipxyAPI._onAddController(this, this.zoomControl)
      }
      // 尺子测距离
      if (this.options.measureCtrl.isShow) {
        this.polylineMeasureControl &&
          this.removeControl(this.polylineMeasureControl)
        this.polylineMeasureControl = new PolylineMeasureExtend({
          isShow: false,
          position: 'topleft',
          unit: 'nauticalmiles',
          measureControlLabel: '&#8614;', // 测量控制按钮的标签（可以使用Unicode符号）。
          clearMeasurementsOnStop: false, // 当关闭测量控制时，是否清除所有已做的测量。
          showMeasurementsClearControl: true,
          //是否显示切换测量单位的控制按钮。
          showUnitControl: true,
          // 是否在提示框中显示方位角信息。
          showBearings: true,
          // 临时虚线的颜色和线宽。
          tempLine: {
            color: '#2F302F',
            weight: 2,
          },
          // arrow.color: 折线中间箭头的颜色。
          //  实线（绘制完成后的折线）的颜色和线宽。
          fixedLine: {
            color: '#2F302F',
            weight: 3,
            stroke: true,
          },
          // startCircle、intermedCircle、currentCircle 和 endCircle: 分别定义起始点、中间点、当前绘制点（绘制过程中）和终点的圆形标记样式，包括颜色、线宽、填充色、填充透明度和半径。
          startCircle: {
            weight: 2,
            color: '#2F302F',
            fillColor: '#88FF88',
            fillOpacity: 1,
            radius: 4,
          },
          intermedCircle: {
            weight: 2,
            color: '#2F302F',
            fillColor: '#FFFFFF',
            fillOpacity: 1,
            radius: 4,
          },
          currentCircle: {
            weight: 2,
            color: '#2F302F',
            fillColor: '#FFFFFF',
            fillOpacity: 1,
            radius: 3,
          },
          endCircle: {
            weight: 2,
            color: '#2F302F',
            fillColor: '#e33647',
            fillOpacity: 1,
            radius: 4,
          },
          tooltipTextFinish: '',
          tooltipTextDelete: '',
          tooltipTextMove: '',
          tooltipTextResume: '',
          tooltipTextAdd: '',
          bindTooltipText: '点击后拖动即可更改',
          changeUnitsText: '更改单位',
          unitControlTitle: {
            //要显示在“单位控制”按钮上的标题文本
            text: '更改单位',
            metres: '米',
            landmiles: '英里',
            nauticalmiles: '海里',
          },
          // unitControlLabel: {
          //   metres: '米',
          //   kilometres: 'km',
          //   feet: 'ft',
          //   landmiles: '英里',
          //   nauticalmiles: '海里'
          // },
          clearControlTitle: '清空测距', // 清除控制按钮的标题文本和标签。
          measureControlTitleOn: '开启测距',
          measureControlTitleOff: '关闭测距', // 分别为开启和关闭测量控制时的标题文本。
          bearingTextIn: '入',
          bearingTextOut: '出', // 分别为进入和离开方向的方位角标签，用于语言相关的提示。
          cursor: `url(${ShipxyOptions.COM_IMG_BASE64.ruler}),crosshair`,
          ...this.options.measureCtrl,
        }).addTo(this)
        this.polylineMeasureControl._resetOptionByMapType(
          this.polylineMeasureControl
        )

        // 2. 监听测距线创建事件
        this.on('polylinemeasure:finish', (event: any) => {
          setTimeout(() => {
            this.polylineMeasureControl.options.finalizeCallBack &&
              this.polylineMeasureControl.options.finalizeCallBack()
          }, 50)
          this.polylineMeasureControl.addCloseButtonToPolyline(event, this)
        })

        ShipxyAPI._onAddController(this, this.polylineMeasureControl)
      }

      this.on('zoomstart', () => {
        if (this.options.zoomAnimation) {
          L.DomUtil.removeClass(this._panes.overlayPane, 'leaflet-zoom-hide')
          L.DomUtil.removeClass(this._panes.markerPane, 'leaflet-zoom-hide')
          L.DomUtil.removeClass(this._panes.tooltipPane, 'leaflet-zoom-hide')
          if (this.getZoom() < 11) {
            L.DomUtil.addClass(this._panes.overlayPane, 'leaflet-zoom-hide')
            L.DomUtil.addClass(this._panes.markerPane, 'leaflet-zoom-hide')
            L.DomUtil.addClass(this._panes.tooltipPane, 'leaflet-zoom-hide')
          }
        }
      })

      this.on('baselayerchange', function () {
        if (this.minimapControl) {
          const tileLayer = L.tileLayer(this.basemapsControl.basemap._url, {
            minZoom: 0,
            maxZoom: 18,
            attribution: '',
          })
          this.minimapControl.changeLayer(tileLayer)
        }
      })
      this.on('resize', () => {
        if (this.shipsService) {
          this.shipsService.resizeHTML()
          this.getSize()
        }
      })
    })

    /**
     * 回调函数
     */
    ShipxyAPI.Map.addInitHook(function () {
      this.options.mapReadyCallBack && this.options.mapReadyCallBack(this)
    })

    ShipxyAPI.Map.addInitHook(function () {
      if (!this.Draw) {
        this.Draw = new DrawUtil(this)
      }
    })
  }

  /**
   * 提供船舶三角函数
   * @param url
   * @param options
   * @returns
   */
  static ShipSjService(url: string, options?: CanvasShipServiceOptions) {
    return canvasShip(url, options)
  }

  // 提供接口服务
  static ShipService(map: MyMap, options?: CanvasShipServiceOptions) {
    map.shipsService ||
      (map.shipsService = new CanvasShipService(map as any, options))
    return map.shipsService
  }
  /**
   * 气象服务
   *   气象服务
        1、气温
        2、气压
        3、风
        4、台风
        5、潮汐
   * @params map 地图对象
   */
  static WeatherService(map: MyMap) {
    map.weatherService || (map.weatherService = new WeatherServiceImpl(map))
    return map.weatherService
  }

  /**
   * 经纬度网格服务
   *   经纬度网格服务，显示经纬度网格。
   * @params map 地图对象
   * @params options 配置信息
   */
  static LatlngGraticuleServer(map: MyMap, options?: OptionsLatLngGraticule) {
    map.latlngGraticuleServer ||
      (map.latlngGraticuleServer = latlngGraticuleServer(map, options))
    return map.latlngGraticuleServer
  }

  /**
   * 晨昏线
   *   经纬度网格服务，显示经纬度网格。
   * @params map 地图对象
   * @params options 配置信息
   */
  static TerminatorSymbolServer(map: MyMap) {
    map.terminatorSymbol || (map.terminatorSymbol = terminatorSymbol(map))
    return map.terminatorSymbol
  }

  /**
    * 特殊区域展示服务
    *   特殊区域展示服务
        1、排放控制区
        2、排放控制区(中国)
        3、专属经济区
        4、全球时区
        5、全球海区
        6、临海基线
    * @params map 地图对象
    * @params options 配置信息
    */
  static AreaViewSymbol(
    map: MyMap,
    options?: Partial<AreaViewSymbolOptions> | undefined
  ) {
    map.areaViewSymbol || (map.areaViewSymbol = areaViewSymbol(map, options))
    return map.areaViewSymbol
  }

  /**
   * 浮标展示服务
   *  浮标展示服务,提供浮标显示、添加浮标、定位浮标等功能。
   * @params map 地图对象
   * @params options 配置信息
   * {
   *  onClick	Object	单击事件，默认：undefined
   *  onHover	Object	悬浮事件，默认：undefined
   *  flyToZoom	Object	定位默认zoom，默认：10
   *  bigIconZoom	Object	大图标显示级别，默认：6
   *  isShow	Object	是否显示航标，默认：true
   *  isShowTooltip	Object	是否显示tooltip，默认：true
   * }
   * @return ShipxyAPI.NavigationServiceImpl | *
   *
   * @example
   *    初始化浮标展示服务
   * ShipxyAPI.NavigationService(elane_map, {
   *        isShow: true,
   *        iconScale: 0.6,
   *        smallIconScale: 0.8,//小图标显示比例
   *        isSelectedShowReal: false,//选中是否显示真实航标
   * });
   */
  static NavigationService(map: MyMap, options: any) {
    if (!map.navigationService) {
      map.navigationService = new NavigationServiceImpl(map, options)
    }
    return map.navigationService
  }

  /**
   * 港口服务
   * @param map
   * @param options
   * @returns
   */
  static PortService(map: MyMap, options?: any) {
    if (!map.portCanvasService) {
      map.portCanvasService = new PortCanvas(map, options)
    }
    return map.portCanvasService
  }
  /**
   * 轨迹服务
   * @param map
   * @param options
   * @returns
   */
  static TrackService(map: MyMap, options?: any) {
    if (!map.trackService) {
      map.trackService = new TrackServiceImpl(map, options)
    }
    return map.trackService
  }

  /**
   * 预测轨迹
   * @param map
   * @param options
   * @returns
   */
  static TrackForecast(map: MyMap) {
    if (!map.trackForecast) {
      map.trackForecast = new TrackForecast(map)
    }
    return map.trackForecast
  }

  /**
   * 航行警告
   * @param map
   * @param options
   * @returns
   */
  static NavigationalWarning(map: MyMap, options?: any) {
    if (!map.navigationalWarning) {
      map.navigationalWarning = new NavigationalWarningService(map, options)
    }
    return map.navigationalWarning
  }

  /**
   * 航线绘制
   * @param map
   * @param data
   * @param options
   * @returns
   */
  static trackDrawSymbol(
    map: any,
    options?: any,
    data?: any
  ): TrackDrawServiceImpl {
    if (!map.trackDrawService) {
      map.trackDrawService = trackDrawServiceImpl(map)
    }
    return map.trackDrawService.addTrackDraw(options, data)
  }

  /**
   * 绘制图层
   * @param map
   * @param options
   * @returns
   */
  static overLayers(map: MyMap, options = {}) {
    if (!map.overLayers) map.overLayers = new OverLayers(map, options)
    return map.overLayers
  }

  /**
   * ais 图层工具展示
   * @param map
   * @param options
   * @returns
   */
  static aisManagerService(map: MyMap, options?: Partial<AisManageOptions>) {
    map.aisManager = map.aisManager || new AisManager(map, options)
    return map.aisManager
  }

  /* >>>>>>>>>>> 其他接口 <<<<<<<<<<*/
  /**
   * 地图定位
   * @param map
   * @param lat
   * @param lng
   * @param layer
   * @returns
   */
  static locationMap(map: MyMap, lat: number, lng: number, layer?: any) {
    ShipxyAPI.removeLocationMap(map, layer)
    const latlon = CanvasShipUtils.GPSEncryptByMapToLatLng(
      L.latLng([lat, lng]),
      map
    )
    map.setView(latlon, map.getZoom())

    return L.marker(latlon, {
      icon: L.icon({
        iconUrl: 'https://cdn.xplusship.com/img/images/center.png',
        iconAnchor: [16, 16],
      }),
    }).addTo(map)
  }

  /**
   * 取消定位
   * @param map
   * @param layer
   */
  static removeLocationMap(map: MyMap, layer?: any) {
    layer && map.removeLayer(layer)
  }
}
